import { ComponentType, SVGProps } from 'react';
import Mail from '@mui/icons-material/Mail';
import Language from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import { SvgIconProps } from '@mui/material/SvgIcon';

type IconLike = ComponentType<SvgIconProps | SVGProps<SVGSVGElement>>;

export type BrandEntry = {
  key: string;
  label: string;
  Icon: IconLike;
  buildHref: (value: string) => string;
};

// Reject schemes that can execute script or exfiltrate context when clicked.
// The registry is driven entirely by user-authored CV data, so an attacker
// could otherwise save `javascript:alert(1)` as a Website value and fire XSS
// on every visitor to the public CV page. httpHref returns an empty string
// for rejected input; callers must treat '' as "do not render the link".
const DANGEROUS_SCHEME = /^(javascript|data|vbscript|file|blob):/i;

const httpHref = (value: string): string => {
  const trimmed = value.trim();
  if (DANGEROUS_SCHEME.test(trimmed)) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

// Platform-aware href builder for social accounts. Accepts three input shapes:
//   1. Full URL  (https://x.com/jane)     → passed through unchanged
//   2. Host/path (x.com/jane)             → routed through httpHref (adds scheme)
//   3. Bare handle, optionally @-prefixed → wrapped into `https://{domain}/{handle}`
// This matters because users type just "thedavidaiken" into a Twitter field
// and expect a working x.com link — the previous behaviour produced a broken
// `https://thedavidaiken` with no host.
const handleHref = (
  domain: string,
  handlePrefix = ''
): ((value: string) => string) => (value) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (DANGEROUS_SCHEME.test(trimmed)) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const stripped = trimmed.replace(/^@/, '');
  if (stripped.includes('/') || stripped.includes('.')) return httpHref(stripped);
  return `https://${domain}/${handlePrefix}${stripped}`;
};

// Bluesky handles are DNS-like (jane.bsky.social) — if the user typed a bare
// word we default to the .bsky.social suffix; if it already contains a dot
// we treat it as a full handle.
const blueskyHref = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (DANGEROUS_SCHEME.test(trimmed)) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const stripped = trimmed.replace(/^@/, '');
  if (stripped.includes('/')) return httpHref(stripped);
  const handle = stripped.includes('.') ? stripped : `${stripped}.bsky.social`;
  return `https://bsky.app/profile/${handle}`;
};

// Email values are interpolated into a mailto: URI. Newlines or tabs in the
// local-part would become header-injection vectors (?bcc=... smuggling) in
// some mail clients; reject them outright.
const mailtoHref = (value: string): string => {
  const trimmed = value.trim();
  if (/[\r\n\t]/.test(trimmed)) return '';
  return `mailto:${trimmed}`;
};

const InstagramSvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>Instagram</title>
    <path d="M12 2.163c3.204 0 3.584.012 4.849.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.265.07 1.645.07 4.849s-.012 3.584-.07 4.849c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.265.058-1.645.07-4.849.07s-3.584-.012-4.849-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.849c.062-1.366.326-2.633 1.301-3.608C4.509 2.568 5.776 2.303 7.142 2.241 8.407 2.183 8.787 2.171 12 2.171zM12 0C8.741 0 8.332.014 7.052.072 5.775.13 4.602.396 3.635 1.363 2.668 2.33 2.402 3.503 2.344 4.78 2.286 6.06 2.272 6.469 2.272 12s.014 5.94.072 7.22c.058 1.277.324 2.45 1.291 3.417.967.967 2.14 1.233 3.417 1.291C8.332 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.277-.058 2.45-.324 3.417-1.291.967-.967 1.233-2.14 1.291-3.417.058-1.28.072-1.689.072-4.948s-.014-3.668-.072-4.948c-.058-1.277-.324-2.45-1.291-3.417C19.398.396 18.225.13 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TwitterSvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>X</title>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const BlueskySvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>Bluesky</title>
    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 01-.415-.056c.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
  </svg>
);

const FacebookSvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>Facebook</title>
    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z"/>
  </svg>
);

const YouTubeSvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>YouTube</title>
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const VimeoSvg = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" role="img" aria-hidden="true" {...props}>
    <title>Vimeo</title>
    <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.59 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797z"/>
  </svg>
);

const BRAND_REGISTRY: readonly BrandEntry[] = [
  { key: 'email', label: 'Email', Icon: Mail, buildHref: mailtoHref },
  { key: 'website', label: 'Website', Icon: Language, buildHref: httpHref },
  { key: 'instagram', label: 'Instagram', Icon: InstagramSvg, buildHref: handleHref('instagram.com') },
  { key: 'twitter', label: 'Twitter', Icon: TwitterSvg, buildHref: handleHref('x.com') },
  { key: 'x', label: 'Twitter', Icon: TwitterSvg, buildHref: handleHref('x.com') },
  { key: 'bluesky', label: 'Bluesky', Icon: BlueskySvg, buildHref: blueskyHref },
  { key: 'facebook', label: 'Facebook', Icon: FacebookSvg, buildHref: handleHref('facebook.com') },
  { key: 'youtube', label: 'YouTube', Icon: YouTubeSvg, buildHref: handleHref('youtube.com', '@') },
  { key: 'vimeo', label: 'Vimeo', Icon: VimeoSvg, buildHref: handleHref('vimeo.com') },
] as const;

export const KNOWN_PLATFORMS: readonly string[] = [
  'Email',
  'Website',
  'Instagram',
  'Twitter',
  'Bluesky',
  'Facebook',
  'YouTube',
  'Vimeo',
] as const;

export const DISPLAY_ORDER: readonly string[] = BRAND_REGISTRY.map((b) => b.key);

export const lookupBrand = (type: string): BrandEntry | null => {
  if (!type) return null;
  const key = type.trim().toLowerCase();
  return BRAND_REGISTRY.find((b) => b.key === key) ?? null;
};

export const fallbackEntry = (label: string): BrandEntry => ({
  key: `unknown:${label.toLowerCase()}`,
  label,
  Icon: LinkIcon,
  buildHref: httpHref,
});
